import { MessageType } from '@shared/protocol';

export type MessageHandler = (type: MessageType, data: DataView) => void;

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
}

export class NetworkClient {
  private ws: WebSocket | null = null;
  private _state = ConnectionState.DISCONNECTED;
  private messageHandler: MessageHandler | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private address = '';
  private onStateChange: ((state: ConnectionState) => void) | null = null;

  get state(): ConnectionState {
    return this._state;
  }

  setMessageHandler(handler: MessageHandler): void {
    this.messageHandler = handler;
  }

  setStateChangeHandler(handler: (state: ConnectionState) => void): void {
    this.onStateChange = handler;
  }

  connect(address: string): void {
    this.address = address;
    this.reconnectAttempts = 0;
    this.doConnect();
  }

  private doConnect(): void {
    if (this.ws) {
      this.ws.close();
    }

    this._state = ConnectionState.CONNECTING;
    this.onStateChange?.(this._state);

    try {
      this.ws = new WebSocket(this.address);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this._state = ConnectionState.CONNECTED;
        this.reconnectAttempts = 0;
        this.onStateChange?.(this._state);
      };

      this.ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer && this.messageHandler) {
          const view = new DataView(event.data);
          const type = view.getUint8(0) as MessageType;
          this.messageHandler(type, view);
        }
      };

      this.ws.onclose = () => {
        this._state = ConnectionState.DISCONNECTED;
        this.onStateChange?.(this._state);

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
          this.reconnectAttempts++;
          setTimeout(() => this.doConnect(), delay);
        }
      };

      this.ws.onerror = () => {
        // Will trigger onclose
      };
    } catch {
      this._state = ConnectionState.DISCONNECTED;
      this.onStateChange?.(this._state);
    }
  }

  send(buffer: ArrayBuffer): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(buffer);
    }
  }

  sendPlayerMove(playerId: number, x: number, y: number, z: number, yaw: number, pitch: number): void {
    const buffer = new ArrayBuffer(21);
    const view = new DataView(buffer);
    view.setUint8(0, MessageType.PLAYER_MOVE);
    view.setUint32(1, playerId);
    view.setFloat32(5, x);
    view.setFloat32(9, y);
    view.setFloat32(13, z);
    view.setFloat32(17, yaw);
    this.send(buffer);
  }

  sendBlockPlace(x: number, y: number, z: number, blockId: number): void {
    const buffer = new ArrayBuffer(12);
    const view = new DataView(buffer);
    view.setUint8(0, MessageType.BLOCK_PLACE);
    view.setInt32(1, x);
    view.setUint16(5, y);
    view.setInt32(7, z);
    view.setUint8(11, blockId);
    this.send(buffer);
  }

  sendBlockBreak(x: number, y: number, z: number): void {
    const buffer = new ArrayBuffer(11);
    const view = new DataView(buffer);
    view.setUint8(0, MessageType.BLOCK_BREAK);
    view.setInt32(1, x);
    view.setUint16(5, y);
    view.setInt32(7, z);
    this.send(buffer);
  }

  sendChat(text: string): void {
    const encoded = new TextEncoder().encode(text);
    const buffer = new ArrayBuffer(1 + encoded.length);
    const view = new DataView(buffer);
    view.setUint8(0, MessageType.CHAT_MESSAGE);
    new Uint8Array(buffer, 1).set(encoded);
    this.send(buffer);
  }

  disconnect(): void {
    this.maxReconnectAttempts = 0; // Prevent reconnection
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._state = ConnectionState.DISCONNECTED;
    this.onStateChange?.(this._state);
  }
}
